// Frontend/src/api/client.ts
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

/**
 * API base URL:
 * - Prefer VITE_API_BASE at build/runtime.
 * - Fallback to relative '/api/v1' so it works behind an edge proxy in prod.
 * - Last resort: localhost (useful for local dev without env).
 */
const API_BASE = '/api/v1';

/**
 * In-memory access token store.
 * - AuthContext (or similar) should call setAccessToken/clearAccessToken.
 * - Refresh token stays in httpOnly cookie (server-managed).
 */
let _accessToken: string | null = null;

export function setAccessToken(token: string | null) {
  _accessToken = token;
}
export function getAccessToken(): string | null {
  return _accessToken;
}
/**
 * The one chokepoint every logout path in this app already calls (LogoutPage,
 * AuthContext's logout() and its two failed-refresh branches, ResidentLayout's
 * own handleLogout). Dispatching a DOM event here -- rather than importing
 * notificationStream.ts directly, which would need to import back from this
 * module for getAccessToken/refreshAccessToken -- lets the stream disconnect
 * on every current and future call site without a circular import.
 */
export function clearAccessToken() {
  _accessToken = null;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("urbe360:logout"));
  }
}

/**
 * Primary Axios instance for API calls.
 * Must send cookies so refresh endpoint can read httpOnly cookie.
 */
const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 20000,
});

/**
 * Bare client (no auth header meddling) to call /auth/refresh
 * and avoid interceptor recursion.
 */
const bare: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 20000,
});

/**
 * Attach Authorization: Bearer <access> if present.
 * Checks in-memory token first, then falls back to sessionStorage.
 */
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Try in-memory token first
  let token = getAccessToken();
  
  // Fallback to sessionStorage (for page refresh scenarios)
  if (!token) {
    token = sessionStorage.getItem('access_token');
    if (token) {
      // Sync in-memory token with sessionStorage
      setAccessToken(token);
    }
  }
  
  // Also check localStorage for resident tokens
  if (!token) {
    token = localStorage.getItem('resident_access_token');
    if (token) {
      setAccessToken(token);
    }
  }
  
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Refresh handling:
 * - On 401 from any request, call POST /auth/refresh/
 * - If refresh succeeds, update token and retry the original request
 * - If refresh fails, bubble the error (caller can log out)
 *
 * We ensure only one refresh runs at a time (single-flight).
 */
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;
let pendingRequests: Array<(token: string) => void> = [];

/**
 * Exported for notificationStream.ts: a 401 on the SSE connection has no
 * axios interceptor to catch it (fetch, not axios -- EventSource cannot set
 * an Authorization header, and fetch's own stream reading has no interceptor
 * layer), so the stream client calls this directly rather than duplicating
 * the single-flight refresh logic.
 */
export async function refreshAccessToken(): Promise<string> {
  if (isRefreshing && refreshPromise) return refreshPromise;

  isRefreshing = true;
  refreshPromise = bare
    .post<{ access: string }>("/auth/refresh/")
    .then((res: AxiosResponse<{ access: string }>) => {
      const newAccess = res.data?.access;
      if (!newAccess) throw new Error("No access token in refresh response");
      setAccessToken(newAccess);
      pendingRequests.forEach((resolve) => resolve(newAccess));
      pendingRequests = [];
      return newAccess;
    })
    .catch((err) => {
      pendingRequests = [];
      clearAccessToken();
      throw err;
    })
    .finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });

  return refreshPromise;
}

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const original = (error.config ?? {}) as AxiosRequestConfig & {
      _retry?: boolean;
      headers?: Record<string, any>;
    };

    // Legacy resident token: multiple properties but no active context in JWT.
    // Force re-login so the user can select a property and get a fresh token.
    if (
      error.response?.status === 403 &&
      (error.response.data as any)?.code === "requires_property_selection"
    ) {
      localStorage.removeItem("resident_access_token");
      localStorage.removeItem("resident_active_membership");
      window.location.href = "/#/login";
      return Promise.reject(error);
    }

    // If no response, not a 401, or already retried, just reject.
    if (!error.response || error.response.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    // prevent infinite loop
    original._retry = true;

    try {
      // enqueue this request behind a single refresh
      const newToken = await new Promise<string>((resolve, reject) => {
        pendingRequests.push(resolve);
        refreshAccessToken().catch(reject);
      });

      // retry original with fresh token
      original.headers = original.headers ?? {};
      original.headers.Authorization = `Bearer ${newToken}`;
      return api(original);
    } catch (refreshErr) {
      return Promise.reject(refreshErr);
    }
  }
);

export default api;
